import type { ZodType } from 'zod'
import { toTypedSchema } from '@vee-validate/zod'
import { camelCase } from 'scule'
import { computed } from 'vue'
import * as z from 'zod'

import { useLocale } from '../../../composables/locale'
import { useLocaleNumber } from '../../../composables/localeNumber'
import { ZodFile } from '../../../lib/ZodFile'
import { ZodHexColor } from '../../../lib/ZodHexColor'

export type FormSchemaCallback<T extends ZodType> = (z: typeof import('zod')) => T

const zExtended = Object.assign({}, z, {
  file: () => ZodFile.create(),
  hexColor: () => ZodHexColor.create(),
})

type ZodRawIssue = Parameters<NonNullable<ReturnType<typeof z.config>['customError']>>[0]

let isErrorMapSet = false

function resolveSchemaMessageSize(size: number) {
  if (size < 1024) {
    return `${size.toFixed(0)} B`
  }
  if (size < (1024 * 1024)) {
    return `${(size / 1024).toFixed(0)} KB`
  }
  return `${(size / (1024 * 1024)).toFixed(0)} MB`
}

export function useFormSchema<T extends ZodType>(cb: FormSchemaCallback<T>) {
  const { t } = useLocale()
  const { formatNumber } = useLocaleNumber()

  if (!isErrorMapSet) {
    z.config({
      customError: (issue: ZodRawIssue) => {
        let message = ''

        if (issue.code === 'custom') {
          message = 'Error.'
          const customIssue = (issue as ZodRawIssue & { params?: { issue?: Record<string, any> } }).params?.issue
          if (customIssue) {
            if (customIssue.code === 'required') {
              message = t('validation.required.default')
            }
            else if (customIssue.code === 'invalid_file_image') {
              message = t('validation.file.image.invalid')
            }
            else if (customIssue.code === 'invalid_file_video') {
              message = t('validation.file.video.invalid')
            }
            else if (customIssue.code === 'invalid_file_image_dimensions') {
              message = t(
                customIssue.type === 'min'
                  ? 'validation.file.image.dimensions.toSmall'
                  : 'validation.file.image.dimensions.toBig',
                {
                  width: `${customIssue.width}px`,
                  height: `${customIssue.height}px`,
                },
              )
            }
            else if (customIssue.code === 'invalid_file_video_dimensions') {
              message = t(
                customIssue.type === 'min'
                  ? 'validation.file.video.dimensions.toSmall'
                  : 'validation.file.video.dimensions.toBig',
                {
                  width: `${customIssue.width}px`,
                  height: `${customIssue.height}px`,
                },
              )
            }
            else if (customIssue.code === 'invalid_file_size') {
              message = t(
                customIssue.type === 'min'
                  ? 'validation.file.size.toSmall'
                  : 'validation.file.size.toBig',
                {
                  size: resolveSchemaMessageSize(customIssue.size),
                },
              )
            }
            else if (customIssue.code === 'invalid_hex_color') {
              message = t('validation.color.hex.invalid')
            }
          }
        }
        else if (issue.code === 'invalid_type') {
          const input = (issue as ZodRawIssue & { input?: unknown }).input
          if (input === undefined || input === null) {
            message = t('validation.required.default')
          }
        }
        else if (issue.code === 'invalid_format') {
          const validation = (issue as ZodRawIssue & { format?: string }).format
          if (validation) {
            if (validation === 'starts_with' || validation === 'ends_with') {
              // TODO
            }
            else {
              message = t(`validation.${camelCase(validation)}.invalid`)
            }
          }
        }
        else if (issue.code === 'too_small') {
          const origin = (issue as ZodRawIssue & { origin?: string }).origin ?? 'any'
          const minValue = (issue as ZodRawIssue & { minimum?: number | bigint }).minimum
          const minNumber = typeof minValue === 'bigint' ? Number(minValue) : minValue
          const min
            = origin === 'date' && typeof minNumber === 'number'
              ? new Date(minNumber)
              : formatNumber(minNumber ?? 0)
          const type = (issue as ZodRawIssue & { exact?: boolean, inclusive?: boolean }).exact
            ? 'exact'
            : (issue as ZodRawIssue & { inclusive?: boolean }).inclusive
                ? 'inclusive'
                : 'notInclusive'
          message = t(
            `validation.${camelCase(origin)}.${type}.toSmall`,
            {
              min,
            },
          )
        }
        else if (issue.code === 'too_big') {
          const origin = (issue as ZodRawIssue & { origin?: string }).origin ?? 'any'
          const maxValue = (issue as ZodRawIssue & { maximum?: number | bigint }).maximum
          const maxNumber = typeof maxValue === 'bigint' ? Number(maxValue) : maxValue
          const max
            = origin === 'date' && typeof maxNumber === 'number'
              ? new Date(maxNumber)
              : formatNumber(maxNumber ?? 0)
          const type = (issue as ZodRawIssue & { exact?: boolean, inclusive?: boolean }).exact
            ? 'exact'
            : (issue as ZodRawIssue & { inclusive?: boolean }).inclusive
                ? 'inclusive'
                : 'notInclusive'
          message = t(
            `validation.${camelCase(origin)}.${type}.toBig`,
            {
              max,
            },
          )
        }

        return message || undefined
      },
    })
    isErrorMapSet = true
  }

  return computed(() => {
    const schema = cb(zExtended)
    return toTypedSchema(schema)
  })
}
